/**
 * Cloud Operations — Bridge agents.
 *
 * Self-Hosted dispatch lives here. Operators register a bridge → server
 * returns a ONE-SHOT registration token + install command → operator runs
 * the install command in their environment → bridge dials wss back and
 * flips to "connected".
 *
 * Per memory: feedback_v2_inline_over_modal — uses an anchored slideout for
 * register, not a modal. The token-display panel is a non-dismissable
 * banner inside that slideout (operator must scroll to ACK + close).
 */

import React, { useEffect, useState } from 'react';
import {
  listBridges, registerBridge, revokeBridge,
} from '../../services/cloudOperationsApi';
import {
  T, btn, btnPrimary, btnSm, btnSmDanger, inp, Field, Banner, StatusPill, ProviderPill,
  Slideout, Th, Td, fmtDate, shortId,
} from './_ui.jsx';

const DEFAULT_REGISTER = {
  name: '',
  provider: 'gcp',
  region: 'us-central1',
};

export default function BridgeAgentsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    listBridges({ size: 50 })
      .then((r) => setRows(r?.items || []))
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div>
      <Header onRegister={() => setRegisterOpen(true)} />

      {error && <Banner color={T.danger} bg={T.dangerBg}>{error}</Banner>}
      {loading && rows.length === 0 ? (
        <Banner color={T.info} bg={T.infoBg}>Loading bridge agents…</Banner>
      ) : rows.length === 0 ? (
        <EmptyState onRegister={() => setRegisterOpen(true)} />
      ) : (
        <BridgesTable rows={rows} onRevoke={async (id) => {
          if (!window.confirm('Revoke this bridge agent? Any in-flight workflows on it will fail.')) return;
          try { await revokeBridge(id); refresh(); }
          catch (e) { setError(e?.message || String(e)); }
        }} />
      )}

      <RegisterSlideout
        open={registerOpen}
        onClose={() => { setRegisterOpen(false); refresh(); }}
      />
    </div>
  );
}

function Header({ onRegister }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, color: T.ink, fontWeight: 700 }}>Bridge agents</h2>
        <div style={{ color: T.text2, fontSize: 13, marginTop: 4 }}>
          Self-Hosted dispatch endpoints. Each bridge is one customer environment that Prove7 reaches via long-lived mTLS.
        </div>
      </div>
      <button type="button" onClick={onRegister} style={btnPrimary}>+ Register bridge</button>
    </div>
  );
}

function EmptyState({ onRegister }) {
  return (
    <div style={{
      background: T.bgCard, border: `1px dashed ${T.stroke}`,
      borderRadius: 10, padding: '40px 24px', textAlign: 'center', color: T.text2,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 6 }}>
        No bridge agents yet
      </div>
      <div style={{ fontSize: 13, marginBottom: 14 }}>
        Self-Hosted deployments dispatch through a customer-installed bridge. Register one to get the install command.
      </div>
      <button type="button" onClick={onRegister} style={btnPrimary}>+ Register bridge</button>
    </div>
  );
}

function BridgesTable({ rows, onRevoke }) {
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead style={{ background: T.bgTint }}>
          <tr>
            <Th>Name</Th>
            <Th>Provider · Region</Th>
            <Th>Status</Th>
            <Th>Version</Th>
            <Th>Last heartbeat</Th>
            <Th>Cert expires</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} style={{ borderTop: `1px solid ${T.stroke2}` }}>
              <Td>
                <div style={{ fontWeight: 600, color: T.ink }}>{b.name}</div>
                <div style={{ fontSize: 11, color: T.text3 }}>{shortId(b.id)}</div>
              </Td>
              <Td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ProviderPill provider={b.provider} />
                  <span style={{ color: T.text2 }}>{b.region}</span>
                </div>
              </Td>
              <Td>
                <StatusPill status={b.status} />
                {b.awaiting_first_connect && (
                  <div style={{ fontSize: 11, color: T.warn, marginTop: 2 }}>
                    awaiting first connect
                  </div>
                )}
              </Td>
              <Td>{b.version || '—'}</Td>
              <Td>{fmtDate(b.last_heartbeat_at)}</Td>
              <Td>{fmtDate(b.mtls_cert_expires_at)}</Td>
              <Td>
                {b.status !== 'revoked' && (
                  <button type="button" onClick={() => onRevoke(b.id)} style={btnSmDanger}>Revoke</button>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RegisterSlideout({ open, onClose }) {
  const [form, setForm] = useState(DEFAULT_REGISTER);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);    // { registration_token, install_command, … }
  const [error, setError] = useState(null);

  // Reset on open so re-opening the slideout doesn't show a stale token.
  useEffect(() => {
    if (open) {
      setForm(DEFAULT_REGISTER);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await registerBridge(form);
      setResult(r);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Slideout open={open} onClose={onClose} width={640}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.stroke}` }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
          {result ? 'Bridge registered' : 'Register Self-Hosted bridge agent'}
        </div>
        <div style={{ fontSize: 12, color: T.text2, marginTop: 4 }}>
          {result
            ? 'Save the token below — it is shown ONCE and never persisted in plaintext.'
            : 'Generates a one-shot registration token + Docker install command.'}
        </div>
      </div>

      <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
        {error && <Banner color={T.danger} bg={T.dangerBg}>{error}</Banner>}

        {!result ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="Name" required hint="Operator-facing label, e.g. acme-prod-us-central.">
              <input
                style={inp}
                placeholder="acme-prod-us-central"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Provider" required>
              <select
                style={inp}
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
              >
                <option value="gcp">GCP</option>
                <option value="aws">AWS</option>
                <option value="azure">Azure</option>
              </select>
            </Field>
            <Field label="Region" required>
              <input
                style={inp}
                placeholder="us-central1"
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
              />
            </Field>
          </div>
        ) : (
          <ResultPanel result={result} />
        )}
      </div>

      <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.stroke}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {!result ? (
          <>
            <button type="button" onClick={onClose} style={btn}>Cancel</button>
            <button type="button" onClick={submit} disabled={submitting} style={btnPrimary}>
              {submitting ? 'Registering…' : 'Register'}
            </button>
          </>
        ) : (
          <button type="button" onClick={onClose} style={btnPrimary}>I saved the token — close</button>
        )}
      </div>
    </Slideout>
  );
}

function ResultPanel({ result }) {
  const [copied, setCopied] = useState({ token: false, cmd: false });
  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied((c) => ({ ...c, [key]: true }));
      setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 2000);
    });
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{
        background: T.warnBg, border: `1px solid ${T.warn}33`, borderLeft: `3px solid ${T.warn}`,
        padding: 12, borderRadius: 4, color: T.warn, fontSize: 13,
      }}>
        <strong>Shown once.</strong> If you close this panel without saving the token, you must revoke and re-register the bridge.
      </div>

      <Section title="Registration token" expires={result.token_expires_at}>
        <CopyBox text={result.registration_token} copied={copied.token} onCopy={() => copy(result.registration_token, 'token')} />
      </Section>

      <Section title="Install command (run in your environment)">
        <CopyBox text={result.install_command} multiline copied={copied.cmd} onCopy={() => copy(result.install_command, 'cmd')} />
      </Section>

      <div style={{ fontSize: 12, color: T.text2 }}>
        Bridge ID: <code>{result.bridge.id}</code>. Status will flip to <strong>connected</strong>
        {' '}once the install runs successfully and the bridge presents the token.
      </div>
    </div>
  );
}

function Section({ title, expires, children }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.ink, textTransform: 'uppercase', letterSpacing: '.06em' }}>
          {title}
        </div>
        {expires && (
          <div style={{ fontSize: 11, color: T.text3 }}>expires {fmtDate(expires)}</div>
        )}
      </div>
      {children}
    </div>
  );
}

function CopyBox({ text, multiline, copied, onCopy }) {
  return (
    <div style={{ position: 'relative' }}>
      <pre style={{
        background: '#0a1d44', color: '#e8f0fb', padding: 12, borderRadius: 4,
        fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        whiteSpace: multiline ? 'pre' : 'pre-wrap', wordBreak: 'break-all',
        margin: 0, maxHeight: 200, overflow: 'auto',
      }}>{text}</pre>
      <button
        type="button"
        onClick={onCopy}
        style={{
          ...btnSm,
          position: 'absolute', top: 8, right: 8,
          background: '#e8f0fb', color: T.ink, borderColor: 'transparent',
        }}
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
    </div>
  );
}
